<template>
    <div class="min-h-screen">
        <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
            <transition name="view-fade" mode="out-in">
                <div v-if="currentView === 'list'" key="list">
                    <header class="mb-8">
                        <h1
                            class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 text-center sm:text-left">
                            Financiamentos
                            <span class="text-brand-600 dark:text-brand-500">& Empréstimos</span>
                        </h1>
                        <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1">
                            Acompanhe suas parcelas e saldo devedor em um só lugar.
                        </p>
                    </header>

                    <div class="mb-8 flex justify-start">
                        <button @click="handleNovoFinanciamento"
                            class="btn-primary flex items-center justify-center text-sm shadow-lg hover:shadow-brand-700/50 transition-all">
                            <span class="material-symbols-outlined mr-2 text-lg">post_add</span>
                            Adicionar Financiamento
                        </button>
                    </div>

                    <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="i in 3" :key="i" class="skeleton-card"></div>
                    </div>

                    <div v-else-if="financiamentos.length === 0" class="empty-state">
                        <span
                            class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">request_quote</span>
                        <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum financiamento cadastrado.
                        </p>
                    </div>

                    <transition-group v-else name="list" tag="div"
                        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="fin in financiamentos" :key="fin.id" @click="selectFinanciamento(fin.id)"
                            class="card-financing group">
                            <div>
                                <h3
                                    class="font-bold text-xl text-slate-800 dark:text-slate-100 truncate group-hover:text-brand-600 dark:group-hover:text-brand-500 transition-colors">
                                    {{ fin.descricao }}</h3>
                                <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">{{ fin.instituicaoFinanceira
                                    }}
                                </p>
                                <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <div class="flex justify-between items-baseline mb-2"><span
                                            class="text-sm font-medium text-slate-500 dark:text-slate-400">Progresso</span><span
                                            class="text-sm font-bold text-brand-600 dark:text-brand-500">{{
                                                fin.parcelasPagas }} / {{ fin.numeroParcelas }}</span></div>
                                    <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                                        <div class="bg-brand-500 h-2.5 rounded-full transition-all duration-500"
                                            :style="{ width: `${fin.numeroParcelas > 0 ? (fin.parcelasPagas / fin.numeroParcelas) * 100 : 0}%` }">
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 text-right">
                                <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">Saldo Devedor</p>
                                <p class="font-bold text-2xl text-slate-800 dark:text-slate-100 tracking-tight">{{
                                    displayCurrency(fin.saldoDevedor) }}</p>
                            </div>
                        </div>
                    </transition-group>
                </div>

                <div v-else-if="currentView === 'details' && selectedFinanciamento" key="details">
                    <button @click="returnToList"
                        class="mb-4 flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-500/80 transition-colors">
                        <span class="material-symbols-outlined mr-1">arrow_back</span>Voltar para a Lista
                    </button>

                    <header class="bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg mb-8">
                        <div class="md:flex justify-between items-start">
                            <div>
                                <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">{{
                                    selectedFinanciamento.descricao }}</h1>
                                <p class="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">{{
                                    selectedFinanciamento.instituicaoFinanceira }}</p>
                            </div>
                            <div class="flex flex-wrap gap-3 mt-4 md:mt-0 flex-shrink-0">
                                <button @click="handleEditFinanciamento" class="btn-secondary flex-1 sm:flex-none">
                                    <span class="material-symbols-outlined mr-2 text-lg">edit</span> Editar
                                </button>
                            </div>
                        </div>
                        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                            <div>
                                <p class="text-sm text-slate-500 dark:text-slate-400">Parcelas Pagas</p>
                                <p class="text-2xl font-bold text-slate-700 dark:text-slate-200">{{
                                    selectedFinanciamento.parcelasPagas }} de {{ selectedFinanciamento.numeroParcelas }}
                                </p>
                            </div>
                            <div>
                                <p class="text-sm text-slate-500 dark:text-slate-400">Total Pago</p>
                                <p class="text-2xl font-bold text-slate-700 dark:text-slate-200">{{
                                    displayCurrency(selectedFinanciamento.totalPago) }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-slate-500 dark:text-slate-400">Saldo Devedor</p>
                                <p class="text-2xl font-bold text-slate-700 dark:text-slate-200">{{
                                    displayCurrency(selectedFinanciamento.saldoDevedor) }}</p>
                            </div>
                        </div>
                    </header>

                    <div class="mb-8"><button @click="showAmortizacaoModal = true"
                            class="btn-primary flex items-center justify-center text-sm w-full sm:w-auto shadow-lg hover:shadow-brand-700/50"><span
                                class="material-symbols-outlined mr-2 text-lg">rocket_launch</span>Fazer Amortização
                            (Extra)</button></div>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Quadro de Parcelas
                            </h2>
                            <div class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg max-h-[600px] overflow-y-auto p-6">
                                <div class="relative timeline-container">
                                    <div v-for="(p, index) in selectedFinanciamento.parcelas" :key="p.id"
                                        class="timeline-item group">
                                        <div class="timeline-point" :class="getTimelinePointClass(p, index)"></div>
                                        <div class="timeline-content">
                                            <div class="flex items-center justify-between">
                                                <div>
                                                    <p class="font-semibold text-slate-800 dark:text-slate-100">Parcela
                                                        #{{
                                                            p.numero }} - {{ displayCurrency(p.valor) }}</p>
                                                    <p class="text-sm text-slate-500 dark:text-slate-400">Vencimento: {{
                                                        formatDate(p.dataVencimento) }}</p>
                                                    <p v-if="p.paga"
                                                        class="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                                        Pago em {{ formatDate(p.dataPagamento) }}</p>
                                                </div>
                                                <div
                                                    class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        v-if="p.numero % 12 === 0 && !p.paga && !hasAdjustmentFor(p.numero)"
                                                        @click="openReajusteModal(p)"
                                                        class="btn-warning !text-xs !py-1 !px-2"
                                                        title="Registrar Reajuste Anual">
                                                        Reajustar
                                                    </button>
                                                    <button v-if="p.paga" @click="confirmMarkAsPaid(p, false)"
                                                        :disabled="updatingParcelaId === p.id"
                                                        class="btn-secondary !text-xs !py-1 !px-2"
                                                        title="Marcar como não paga">Desfazer</button>
                                                    <button v-else @click="confirmMarkAsPaid(p, true)"
                                                        :disabled="updatingParcelaId === p.id"
                                                        class="btn-success text-xs !py-1 !px-2 flex items-center">
                                                        <span v-if="updatingParcelaId === p.id"
                                                            class="animate-spin material-symbols-outlined text-sm mr-1">progress_activity</span>
                                                        Pagar
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="space-y-8">
                            <div>
                                <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Amortizações
                                </h2>
                                <div v-if="!selectedFinanciamento.amortizacoes || selectedFinanciamento.amortizacoes.length === 0"
                                    class="empty-state !py-12">
                                    <span
                                        class="material-symbols-outlined text-5xl text-brand-400 dark:text-brand-700">history</span>
                                    <p class="mt-2 font-medium text-sm">Nenhum pagamento extra.</p>
                                </div>
                                <div v-else
                                    class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg max-h-[250px] overflow-y-auto">
                                    <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                                        <li v-for="a in selectedFinanciamento.amortizacoes" :key="a.id"
                                            class="p-4 flex items-center">
                                            <div
                                                class="w-10 h-10 rounded-full bg-slate-100 dark:bg-brand-700 text-brand-600 dark:text-brand-500 flex items-center justify-center flex-shrink-0">
                                                <span class="material-symbols-outlined">rocket_launch</span>
                                            </div>
                                            <div class="ml-4">
                                                <p class="font-semibold text-slate-800 dark:text-slate-100">{{
                                                    displayCurrency(a.valor) }}</p>
                                                <p class="text-sm text-slate-500 dark:text-slate-400">Em {{
                                                    formatDate(a.data) }}</p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Histórico de
                                    Reajustes</h2>
                                <div v-if="!selectedFinanciamento.ajustes || selectedFinanciamento.ajustes.length === 0"
                                    class="empty-state !py-12">
                                    <span
                                        class="material-symbols-outlined text-5xl text-brand-400 dark:text-brand-700">trending_up</span>
                                    <p class="mt-2 font-medium text-sm">Nenhum reajuste registrado.</p>
                                </div>
                                <div v-else
                                    class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg max-h-[250px] overflow-y-auto">
                                    <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                                        <li v-for="ajuste in selectedFinanciamento.ajustes" :key="ajuste.id"
                                            class="p-4">
                                            <p class="font-semibold text-slate-800 dark:text-slate-100">
                                                Reajuste de {{ ajuste.indiceAplicado.toFixed(2) }}% em {{
                                                    formatDate(ajuste.dataAjuste) }}
                                            </p>
                                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                                Parcelas a partir da #{{ ajuste.numeroParcelaReferencia + 1 }} para {{
                                                    displayCurrency(ajuste.novoValorParcela) }}.
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Zona de Perigo
                                </h2>
                                <div class="danger-zone">
                                    <div>
                                        <h4 class="font-bold text-red-800 dark:text-red-300">Excluir Financiamento</h4>
                                        <p class="text-sm text-red-600 dark:text-red-300/80 mb-2">Esta ação é
                                            irreversível e excluirá
                                            todo o histórico.</p>
                                        <button @click="confirmDelete"
                                            class="btn-danger-outline flex items-center text-sm">
                                            <span class="material-symbols-outlined mr-2 text-lg">delete_forever</span>
                                            Excluir Financiamento
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showCreateModal" @click.self="showCreateModal = false" class="modal-backdrop">
                     <div class="modal-container">
                         <header class="modal-header">
                             <h3>Adicionar Financiamento</h3><button @click="showCreateModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                         </header>
                         <form @submit.prevent="handleCreate" class="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
                             <div class="sm:col-span-2"><label class="form-label">Descrição</label><input v-model="form.descricao" type="text" required placeholder="Ex: Financiamento Veículo" class="form-input" /></div>
                             <div><label class="form-label">Valor Total (R$)</label><input v-model.number="form.valorTotal" type="number" step="0.01" required class="form-input" /></div>
                             <div><label class="form-label">Taxa de Juros Anual (%)</label><input v-model.number="form.taxaDeJurosAnual" type="number" step="0.01" placeholder="Ex: 9.99" class="form-input" /></div>
                             <div><label class="form-label">Prazo (Anos)</label><input v-model.number="form.numeroDeAnos" type="number" placeholder="Ex: 5" class="form-input" /></div>
                             <div><label class="form-label">Nº de Parcelas</label><input v-model.number="form.numeroParcelas" type="number" required class="form-input" /></div>
                             <div class="relative">
                                 <label class="form-label">Valor da Parcela (R$)</label>
                                 <input v-model.number="form.valorParcela" type="number" step="0.01" required class="form-input" :readonly="isValorParcelaCalculated" :class="{ 'form-input-readonly': isValorParcelaCalculated }" />
                                 <span v-if="isValorParcelaCalculated" class="material-symbols-outlined absolute right-3 top-9 text-slate-400 text-base" title="Valor calculado automaticamente">lock</span>
                             </div>
                             <div><label class="form-label">Data Início Contrato</label><input v-model="form.dataInicio" type="date" required class="form-input" /></div>
                             <div class="sm:col-span-2"><label class="form-label">Instituição Financeira</label><input v-model="form.instituicaoFinanceira" type="text" placeholder="Ex: Banco Itaú" class="form-input" /></div>
                             <footer class="sm:col-span-2 modal-footer"><button @click="showCreateModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Salvando...' : 'Salvar' }}</button></footer>
                         </form>
                     </div>
                </div>
            </transition>

            <transition name="modal-fade">
                 <div v-if="showEditModal" @click.self="showEditModal = false" class="modal-backdrop">
                     <div class="modal-container">
                         <header class="modal-header">
                             <h3>Editar Financiamento</h3><button @click="showEditModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                         </header>
                         <form @submit.prevent="handleUpdate" class="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
                             <div class="sm:col-span-2"><label class="form-label">Descrição</label><input v-model="editForm.descricao" type="text" required class="form-input" /></div>
                             <div class="sm:col-span-2"><label class="form-label">Instituição Financeira</label><input v-model="editForm.instituicaoFinanceira" type="text" class="form-input" /></div>
                             <div><label class="form-label">Valor Total (R$)</label><input v-model.number="editForm.valorTotal" type="number" step="0.01" required class="form-input" :disabled="hasPaidInstallments" :class="{ 'form-input-readonly': hasPaidInstallments }" /></div>
                             <div><label class="form-label">Taxa de Juros Anual (%)</label><input v-model.number="editForm.taxaDeJurosAnual" type="number" step="0.01" class="form-input" :disabled="hasPaidInstallments" :class="{ 'form-input-readonly': hasPaidInstallments }" /></div>
                             <div><label class="form-label">Nº de Parcelas</label><input v-model.number="editForm.numeroParcelas" type="number" required class="form-input" :disabled="hasPaidInstallments" :class="{ 'form-input-readonly': hasPaidInstallments }" /></div>
                             <div class="relative">
                                 <label class="form-label">Valor da Parcela (R$)</label>
                                 <input v-model.number="editForm.valorParcela" type="number" step="0.01" required class="form-input" :readonly="isEditValorParcelaCalculated || hasPaidInstallments" :class="{ 'form-input-readonly': isEditValorParcelaCalculated || hasPaidInstallments }" />
                                 <span v-if="isEditValorParcelaCalculated || hasPaidInstallments" class="material-symbols-outlined absolute right-3 top-9 text-slate-400 text-base" title="Campo bloqueado">lock</span>
                             </div>
                             <div><label class="form-label">Data Início Contrato</label><input v-model="editForm.dataInicio" type="date" required class="form-input" :disabled="hasPaidInstallments" :class="{ 'form-input-readonly': hasPaidInstallments }" /></div>
                             <p v-if="hasPaidInstallments" class="sm:col-span-2 text-xs text-brand-600 dark:text-brand-500 p-3 bg-slate-50 dark:bg-brand-700/20 rounded-md"> Campos financeiros não podem ser alterados pois já existem parcelas pagas.</p>
                             <footer class="sm:col-span-2 modal-footer"><button @click="showEditModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Salvando...' : 'Salvar Alterações' }}</button></footer>
                         </form>
                     </div>
                 </div>
            </transition>
             <transition name="modal-fade"> <div v-if="showAmortizacaoModal" @click.self="showAmortizacaoModal = false" class="modal-backdrop"> <div class="modal-container"> <header class="modal-header"> <h3>Fazer Amortização</h3><button @click="showAmortizacaoModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button> </header> <form @submit.prevent="handleAmortizacao" class="p-6 space-y-4"> <div><label class="form-label">Valor do Pagamento Extra (R$)</label><input v-model.number="amortizacaoForm.valor" type="number" step="0.01" required class="form-input" /></div> <p class="text-xs text-slate-500">Este valor será usado para quitar as últimas parcelas em aberto do seu financiamento.</p> <footer class="modal-footer"><button @click="showAmortizacaoModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Amortizando...' : 'Confirmar Pagamento' }}</button></footer> </form> </div> </div> </transition>
             <transition name="modal-fade"> <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false" class="modal-backdrop"> <div class="modal-container max-w-md p-0"> <div class="p-6 text-center"><span class="material-symbols-outlined text-5xl text-red-400 mx-auto">delete_forever</span> <h3 class="modal-title">Excluir Financiamento?</h3> <p class="modal-text">Tem certeza que deseja excluir permanentemente o registro de "{{ selectedFinanciamento?.descricao }}"?</p> </div> <div class="modal-footer"><button @click="showDeleteConfirm = false" type="button" class="btn-secondary">Cancelar</button><button @click="executeDelete" class="btn-danger">Sim, Excluir</button></div> </div> </div> </transition>
             <transition name="modal-fade"> <div v-if="parcelaToUpdate" @click.self="parcelaToUpdate = null" class="modal-backdrop"> <div class="modal-container max-w-md p-0"> <div class="p-6 text-center"><span class="material-symbols-outlined text-5xl text-brand-500 mx-auto">help_center</span> <h3 class="modal-title">Confirmar Alteração</h3> <p class="modal-text">Deseja marcar a parcela <strong>#{{ parcelaToUpdate.parcela.numero }}</strong> como <strong>{{ parcelaToUpdate.isPaid ? 'paga' : 'não paga' }}</strong>?</p> </div> <div class="modal-footer"><button @click="parcelaToUpdate = null" type="button" class="btn-secondary">Cancelar</button><button @click="executeMarkAsPaid" class="btn-primary">Sim, Confirmar</button></div> </div> </div> </transition>
             <transition name="modal-fade"> <div v-if="showReajusteModal" @click.self="showReajusteModal = false" class="modal-backdrop"> <div class="modal-container"> <header class="modal-header"> <h3>Reajuste Anual de Parcelas</h3> <button @click="showReajusteModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button> </header> <form @submit.prevent="handleReajuste" class="p-6 space-y-4"> <p class="text-sm text-slate-600 dark:text-slate-300">Você está aplicando um reajuste após o pagamento da parcela <strong>#{{ parcelaParaReajuste?.numero }}</strong>. As parcelas seguintes serão recalculadas.</p> <div><label class="form-label">Índice de Correção (Ex: IPCA) em %</label><input v-model.number="reajusteForm.indice" type="number" step="0.01" required class="form-input" placeholder="Ex: 4.51" /></div> <footer class="modal-footer"><button @click="showReajusteModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Reajustando...' : 'Confirmar Reajuste' }}</button></footer> </form> </div> </div> </transition>

            <transition name="notification">
                <div v-if="notification.show" class="fixed top-5 right-5 w-full max-w-sm z-[70]">
                    <div class="p-4 rounded-lg shadow-2xl flex items-start text-white"
                        :class="notification.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'">
                        <div class="flex-shrink-0"><span class="material-symbols-outlined">{{ notification.type ===
                            'success' ?
                            'check_circle' : 'error' }}</span></div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm font-bold">{{ notification.message }}</p>
                        </div>
                        <div class="ml-auto pl-3"><button @click="notification.show = false"
                                class="-mx-1.5 -my-1.5 p-1.5 rounded-md hover:bg-black/20 focus:outline-none focus:ring-2 focus:ring-white"><span
                                    class="material-symbols-outlined text-lg">close</span></button></div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const financiamentos = ref([])
const selectedFinanciamento = ref(null)
const currentView = ref('list')
const isLoading = ref(true)
const isSubmitting = ref(false)
const updatingParcelaId = ref(null)

const isMobile = ref(false)
const checkDevice = () => { isMobile.value = window.innerWidth < 768 }

const showCreateModal = ref(false)
const showEditModal = ref(false)
const showDeleteConfirm = ref(false)
const showAmortizacaoModal = ref(false)
const parcelaToUpdate = ref(null)
const showReajusteModal = ref(false)
const parcelaParaReajuste = ref(null)
const reajusteForm = ref({ indice: null })
const notification = ref({ show: false, type: 'success', message: '' })
const notificationTimer = ref(null)

const getInitialFormData = () => ({
  descricao: '',
  valorTotal: null,
  numeroParcelas: null,
  valorParcela: null,
  dataInicio: new Date().toISOString().split('T')[0],
  instituicaoFinanceira: '',
  taxaDeJurosAnual: null,
  numeroDeAnos: null
})
const form = ref(getInitialFormData())
const editForm = ref({
  descricao: '',
  instituicaoFinanceira: '',
  valorTotal: null,
  numeroParcelas: null,
  valorParcela: null,
  dataInicio: '',
  taxaDeJurosAnual: null
})
const amortizacaoForm = ref({ valor: null })

const hasPaidInstallments = computed(() => selectedFinanciamento.value?.parcelasPagas > 0)
const nextInstallmentIndex = computed(() => selectedFinanciamento.value?.parcelas.findIndex(p => !p.paga) ?? -1)
const isValorParcelaCalculated = computed(() => {
  const f = form.value
  return f.valorTotal > 0 && f.numeroParcelas > 0 && f.taxaDeJurosAnual > 0
})
const isEditValorParcelaCalculated = computed(() => {
  const ef = editForm.value
  return ef.valorTotal > 0 && ef.numeroParcelas > 0 && ef.taxaDeJurosAnual > 0
})

const API_BASE_URL = 'https://backend.controlease.com.br/api'
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })

const showNotification = (type, message, duration = 4000) => {
  if (notificationTimer.value) clearTimeout(notificationTimer.value)
  notification.value = { show: true, type, message }
  notificationTimer.value = setTimeout(() => { notification.value.show = false }, duration)
}

const calculateInstallment = (valorTotal, numParcelas, taxaAnual) => {
  if (!(valorTotal > 0 && numParcelas > 0)) return null
  if (!taxaAnual || taxaAnual <= 0) return parseFloat((valorTotal / numParcelas).toFixed(2))
  const taxaMensal = taxaAnual / 12 / 100
  const pmt = valorTotal * (taxaMensal * Math.pow(1 + taxaMensal, numParcelas)) / (Math.pow(1 + taxaMensal, numParcelas) - 1)
  return parseFloat(pmt.toFixed(2))
}

const displayCurrency = value => value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? 'R$ 0,00'
const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(date)
}

const getTimelinePointClass = (parcela, index) => {
  if (parcela.paga) return 'paid'
  if (index === nextInstallmentIndex.value) return 'next'
  return 'pending'
}
const hasAdjustmentFor = parcelaNumero => selectedFinanciamento.value?.ajustes.some(a => a.numeroParcelaReferencia === parcelaNumero)

const fetchFinanciamentos = async () => {
  isLoading.value = true
  try {
    const { data } = await axios.get(`${API_BASE_URL}/financiamentos`, authConfig())
    financiamentos.value = data
  } catch { showNotification('error', 'Não foi possível carregar os financiamentos.') }
  finally { isLoading.value = false }
}

const refreshSelectedFinanciamento = async () => {
  if (!selectedFinanciamento.value?.id) return
  try {
    const { data } = await axios.get(`${API_BASE_URL}/financiamentos/${selectedFinanciamento.value.id}`, authConfig())
    selectedFinanciamento.value = data
  } catch {
    showNotification('error', 'Erro ao atualizar os dados.')
    returnToList()
  }
}

const selectFinanciamento = async id => {
  const currentId = selectedFinanciamento.value?.id
  if (String(currentId) === String(id) && currentView.value === 'details') return
  isLoading.value = true
  currentView.value = 'details'
  try {
    const { data } = await axios.get(`${API_BASE_URL}/financiamentos/${id}`, authConfig())
    selectedFinanciamento.value = data
    if (route.name === 'FinanciamentosUser' && route.params.financiamentoId !== String(id)) {
      router.push({ name: 'FinanciamentosUser', params: { financiamentoId: id } })
    }
  } catch {
    showNotification('error', 'Não foi possível carregar os detalhes.')
    router.push({ name: 'FinanciamentosUser' })
    currentView.value = 'list'
  } finally { isLoading.value = false }
}

const returnToList = async (updateRoute = true) => {
  currentView.value = 'list'
  selectedFinanciamento.value = null
  if (updateRoute && route.name === 'FinanciamentosUser' && route.params.financiamentoId) {
    await router.push({ name: 'FinanciamentosUser' })
  }
  if (updateRoute) await fetchFinanciamentos()
}

watch(
  () => route.params.financiamentoId,
  (newId, oldId) => {
    if (route.name !== 'FinanciamentosUser') return
    const strNew = String(newId ?? '')
    const strOld = String(oldId ?? '')
    if (newId && strNew !== strOld) {
      selectFinanciamento(newId)
    } else if (oldId && !newId) {
      returnToList(false)
    }
  },
  { immediate: true }
)

watch(() => form.value.numeroDeAnos, newAnos => { if (newAnos > 0) form.value.numeroParcelas = newAnos * 12 })
watch(
  [() => form.value.valorTotal, () => form.value.numeroParcelas, () => form.value.taxaDeJurosAnual],
  ([total, parcelas, taxa]) => { form.value.valorParcela = calculateInstallment(total, parcelas, taxa) }
)
watch(() => editForm.value.numeroDeAnos, newAnos => {
  if (newAnos > 0 && !hasPaidInstallments.value) editForm.value.numeroParcelas = newAnos * 12
})
watch(
  [() => editForm.value.valorTotal, () => editForm.value.numeroParcelas, () => editForm.value.taxaDeJurosAnual],
  ([total, parcelas, taxa]) => { if (!hasPaidInstallments.value) editForm.value.valorParcela = calculateInstallment(total, parcelas, taxa) }
)

const handleNovoFinanciamento = () => {
  if (isMobile.value) {
    router.push({ name: 'FinanciamentoAdd' })
  } else {
    openCreateModal()
  }
}

const handleEditFinanciamento = () => {
  if (!selectedFinanciamento.value) return
  if (isMobile.value) {
    router.push({ name: 'FinanciamentoEdit', params: { financiamentoId: selectedFinanciamento.value.id } })
  } else {
    openEditModal()
  }
}

const openCreateModal = () => {
  form.value = getInitialFormData()
  showCreateModal.value = true
}

const handleCreate = async () => {
  isSubmitting.value = true
  try {
    const { data } = await axios.post(`${API_BASE_URL}/financiamentos`, form.value, authConfig())
    showCreateModal.value = false
    form.value = getInitialFormData()
    await fetchFinanciamentos()
    showNotification('success', 'Financiamento criado com sucesso!')
    if (data?.id) await selectFinanciamento(data.id)
  } catch (e) { showNotification('error', e.response?.data?.message || 'Erro ao criar financiamento.') }
  finally { isSubmitting.value = false }
}

const openEditModal = () => {
  if (!selectedFinanciamento.value) return
  const s = selectedFinanciamento.value
  editForm.value = {
    descricao: s.descricao,
    instituicaoFinanceira: s.instituicaoFinanceira,
    valorTotal: s.valorTotal,
    numeroParcelas: s.numeroParcelas,
    valorParcela: s.valorParcela,
    dataInicio: s.dataInicio,
    taxaDeJurosAnual: s.taxaDeJurosAnual
  }
  showEditModal.value = true
}

const handleUpdate = async () => {
  isSubmitting.value = true
  try {
    await axios.put(`${API_BASE_URL}/financiamentos/${selectedFinanciamento.value.id}`, editForm.value, authConfig())
    showEditModal.value = false
    await refreshSelectedFinanciamento()
    showNotification('success', 'Financiamento atualizado com sucesso!')
  } catch (e) { showNotification('error', e.response?.data?.message || 'Erro ao atualizar.') }
  finally { isSubmitting.value = false }
}

const confirmDelete = () => { showDeleteConfirm.value = true }
const executeDelete = async () => {
  showDeleteConfirm.value = false
  try {
    await axios.delete(`${API_BASE_URL}/financiamentos/${selectedFinanciamento.value.id}`, authConfig())
    showNotification('success', 'Financiamento excluído com sucesso.')
    await returnToList()
  } catch { showNotification('error', 'Erro ao excluir financiamento.') }
}

const confirmMarkAsPaid = (parcela, isPaid) => { parcelaToUpdate.value = { parcela, isPaid } }
const executeMarkAsPaid = async () => {
  if (!parcelaToUpdate.value) return
  const { parcela, isPaid } = parcelaToUpdate.value
  updatingParcelaId.value = parcela.id
  parcelaToUpdate.value = null
  try {
    await axios.patch(`${API_BASE_URL}/financiamentos/parcelas/${parcela.id}`, { paga: isPaid }, authConfig())
    await refreshSelectedFinanciamento()
    showNotification('success', `Parcela #${parcela.numero} atualizada.`)
  } catch { showNotification('error', 'Erro ao alterar status da parcela.') }
  finally { updatingParcelaId.value = null }
}

const openReajusteModal = parcela => {
  parcelaParaReajuste.value = parcela
  reajusteForm.value.indice = null
  showReajusteModal.value = true
}
const handleReajuste = async () => {
  if (!reajusteForm.value.indice || !parcelaParaReajuste.value) return
  isSubmitting.value = true
  try {
    const payload = { indice: reajusteForm.value.indice, numeroParcelaReferencia: parcelaParaReajuste.value.numero }
    await axios.post(`${API_BASE_URL}/financiamentos/${selectedFinanciamento.value.id}/reajustar`, payload, authConfig())
    showReajusteModal.value = false
    await refreshSelectedFinanciamento()
    showNotification('success', 'Reajuste aplicado com sucesso!')
  } catch (e) { showNotification('error', e.response?.data?.message || 'Erro ao registrar reajuste.') }
  finally { isSubmitting.value = false }
}

onMounted(async () => {
  checkDevice()
  window.addEventListener('resize', checkDevice)
  if (route.name === 'FinanciamentosUser' && route.params.financiamentoId) {
    await selectFinanciamento(route.params.financiamentoId)
  } else {
    currentView.value = 'list'
    await fetchFinanciamentos()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', checkDevice)
  if (notificationTimer.value) clearTimeout(notificationTimer.value)
})
</script>

<style scoped>
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1; }
.form-input { @apply w-full px-3 py-2 text-sm bg-white dark:bg-slate-700/60 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200; }
.form-input-readonly { @apply bg-slate-100 dark:bg-brand-800/50 cursor-not-allowed opacity-70; }
.btn-primary { @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed; }
.btn-secondary { @apply px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed; }
.btn-success { @apply px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed; }
.btn-danger { @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out; }
.btn-danger-outline { @apply px-5 py-2.5 text-sm font-medium text-red-600 bg-transparent border border-red-500 hover:bg-red-50 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed; }
.btn-warning { @apply bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/50; }
.card-financing { @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg flex flex-col justify-between transition-all duration-300 hover:shadow-xl dark:hover:shadow-brand-700/30 cursor-pointer hover:-translate-y-1; }
.empty-state { @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col justify-center items-center; }
.skeleton-card { @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg animate-pulse h-52; }
.danger-zone { @apply bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 p-5 rounded-2xl flex flex-col items-start gap-4; }
.modal-backdrop { @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 dark:bg-brand-900/80 backdrop-blur-sm p-4; }
.modal-container { @apply bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg transform transition-all; }
.modal-header { @apply px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center text-xl font-semibold text-slate-800 dark:text-slate-100; }
.modal-close-btn { @apply p-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors; }
.modal-footer { @apply p-4 bg-slate-50 dark:bg-slate-700/50 flex justify-end space-x-3 rounded-b-xl; }
.modal-title { @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100; }
.modal-text { @apply mt-2 text-sm text-slate-600 dark:text-slate-400; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; transform: scale(0.95) translateY(-20px); }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.view-fade-enter-active, .view-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.view-fade-enter-from { opacity: 0; transform: translateX(-10px); }
.view-fade-leave-to { opacity: 0; transform: translateX(10px); }
.list-enter-active, .list-leave-active { transition: all 0.4s ease; }
.list-enter-from, .list-leave-to { opacity: 0; transform: translateY(20px); }
.timeline-container { @apply border-l-2 border-slate-200 dark:border-slate-700 ml-5; }
.timeline-item { @apply relative pl-10 py-4; }
.timeline-point { @apply absolute -left-[11px] top-[26px] w-5 h-5 rounded-full border-4 border-white dark:border-brand-800; }
.timeline-point.paid { @apply bg-emerald-500; }
.timeline-point.pending { @apply bg-slate-300 dark:bg-slate-600; }
.timeline-point.next { @apply bg-brand-600 ring-4 ring-brand-500/30; }
.notification-enter-active, .notification-leave-active { transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1); }
.notification-enter-from, .notification-leave-to { opacity: 0; transform: translateX(100%); }
</style>